
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  mapRefereeOnboardingFormForBackend,
  mapRefereeOnboardingFormForToFrontend,
  refereeOnboardingFormSchema,
} from "./types";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button, Checkbox, Form, FormField, FormInput, Label } from "../ui";
import { ActivityIndicator, View } from "react-native";
import { Recruitment } from "../types/Recruitment";
import { P } from "../ui/typography";
import { trpc } from "@/trpc/react";
import { router } from "expo-router";
import Toast from "react-native-toast-message";
import FileInput, { FileProps } from "../file-input";
import { supabase } from "@/lib/supabase";
import { decode } from 'base64-arraybuffer';

interface RefereeFormProps {
  recruitmentData: Recruitment
  onFormSuccess: () => void
}

export default function RefereeForm({ recruitmentData, onFormSuccess }: RefereeFormProps) {
  const form = useForm<z.infer<typeof refereeOnboardingFormSchema>>({
    resolver: zodResolver(refereeOnboardingFormSchema),
    defaultValues: mapRefereeOnboardingFormForToFrontend(recruitmentData)
  });

  const { mutate: updateRecruitment } = trpc.recruitment.update.useMutation({
    onSuccess(_response) {
      onFormSuccess();
      router.back();
      Toast.show({
        type: 'success',
        text1: 'Successfully referee details'
      });
    },
  });

  async function onSubmit(data: z.infer<typeof refereeOnboardingFormSchema>) {
    const mappedDetails = mapRefereeOnboardingFormForBackend(recruitmentData.id, data);
    updateRecruitment(mappedDetails);
  }

  async function onFileSelect(file: FileProps) {
    const { error: uploadError } = await supabase.storage
      .from("recruitment")
      .upload(`${recruitmentData.id}/cv`, decode(file.base64EncodedFile), { contentType: file.type, upsert: true });

    if (uploadError) {
      Toast.show({
        type: 'error',
        text1: 'Failed to save file.'
      });
    }
  }

  return (
    <Card className='mt-4 mb-20'>
      <CardContent>
        <Form {...form}>
          <View className="gap-4 mt-8">
            <FormField
              control={form.control}
              name="referee_name"
              render={({ field }) => (
                <FormInput
                  label="Name of referee"
                  {...field}
                />
              )}
            />
            <FormField
              control={form.control}
              name="referee_phone"
              render={({ field }) => (
                <FormInput
                  label="Referee contact number"
                  keyboardType="phone-pad"
                  {...field}
                />
              )}
            />
            <Label>Upload your CV</Label>
            <FileInput onFileSelect={(file: FileProps | FileProps[]) => {
              if (!Array.isArray(file)) {
                onFileSelect(file)
              }
            }} />
            <FormField
              control={form.control}
              name="marketing"
              render={({ field }) => (
                <View className='flex-row gap-3 items-start'>
                  <Checkbox aria-labelledby='terms' checked={field.value || false} onCheckedChange={(check) => field.onChange(check)} />
                  <Label nativeID='terms' onPress={() => field.onChange(!field.value)} className='flex-1'>
                    Are you happy to receive marketing and news via email from us?
                  </Label>
                </View>
              )}
            />
            <P>I confirm that to the best of my knowledge the information I have provided on this form is correct and I accept that providing
              deliberately false information could result in my application being rejected or my employment being terminated.
            </P>
            <FormField
              control={form.control}
              name="t_c_agreement"
              render={({ field }) => (
                <View className='flex-row gap-3 items-center'>
                  <Checkbox aria-labelledby='terms' checked={field.value || false} onCheckedChange={(check) => field.onChange(check)} />
                  <Label nativeID='terms' onPress={() => field.onChange(!field.value)}>
                    I Understand
                  </Label>
                </View>
              )}
            />
          </View>
          <Button
            size="default"
            variant="crew"
            onPress={form.handleSubmit(onSubmit)}
            className="mt-4"
          >
            {form.formState.isSubmitting ? (
              <ActivityIndicator size="small" />
            ) : (
              "Save"
            )}
          </Button>
        </Form>
      </CardContent>
    </Card >
  )

}